import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import { Wrench, Clock, Shield, HeadphonesIcon } from "lucide-react";
import { SeoHead } from '@/components/SeoHead';

export default function MaintenanceSupport() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "Preventive Maintenance",
      description: "Regular system updates, security patches, and performance optimization to prevent issues.",
      icon: <Wrench className="h-8 w-8" />
    },
    {
      title: "24/7 Monitoring",
      description: "Round-the-clock system monitoring with proactive issue detection and resolution.",
      icon: <Clock className="h-8 w-8" />
    },
    {
      title: "SLA Agreements",
      description: "Service level agreements ensuring guaranteed response times and system uptime.",
      icon: <Shield className="h-8 w-8" />
    },
    {
      title: "Technical Support",
      description: "Expert technical support for troubleshooting and resolving system issues quickly.",
      icon: <HeadphonesIcon className="h-8 w-8" />
    }
  ];

  const supportPlans = [
    {
      name: "Basic Support",
      price: "$99",
      period: "per month",
      features: ["Business Hours Support", "Email Support", "Basic Monitoring", "Monthly Reports"],
      responseTime: "8 hours"
    },
    {
      name: "Premium Support",
      price: "$199",
      period: "per month",
      features: ["24/7 Support", "Phone & Email Support", "Advanced Monitoring", "Weekly Reports", "Priority Queue"],
      responseTime: "2 hours",
      popular: true
    },
    {
      name: "Enterprise Support",
      price: "$399",
      period: "per month",
      features: ["Dedicated Support Manager", "24/7 Phone Support", "Proactive Monitoring", "Real-time Reports", "Custom SLA"],
      responseTime: "30 minutes"
    }
  ];

  const maintenanceAreas = [
    { name: "Website Maintenance", icon: "fas fa-globe", description: "Regular updates and security patches" },
    { name: "Server Management", icon: "fas fa-server", description: "Server monitoring and optimization" },
    { name: "Database Optimization", icon: "fas fa-database", description: "Database performance tuning" },
    { name: "Security Updates", icon: "fas fa-shield-alt", description: "Regular security patches and updates" },
    { name: "Backup Management", icon: "fas fa-save", description: "Automated backups and recovery" },
    { name: "Performance Monitoring", icon: "fas fa-chart-line", description: "System performance analysis" }
  ];

  return (
    <>
      <SeoHead 
        title="IT Maintenance & Support UAE | ZeeX Soft"
        description="Reliable ongoing IT maintenance and technical support in Dubai & Sharjah. Proactive monitoring, updates, and 24/7 assistance."
        keywords="it maintenance uae, tech support dubai, managed services sharjah"
      />

      <main>
        

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-teal-900 to-blue-900 py-24 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto text-center relative z-10"
          >
            <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-300 to-blue-400 mb-6">
            Hassle-free <span className="text-blue-600">IT Support</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
            Proactive maintenance and reliable technical support for your business
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                onClick={openDrawer}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Get Support Plan
              </Button>
              <Button 
                asChild
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg font-medium rounded-lg transition-all duration-300"
              >
                <Link href="/contact">
                  Contact Our Team
                </Link>
              </Button>
            </div>
          </motion.div>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-64 h-64 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Support Services</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Comprehensive maintenance and support solutions for peace of mind
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-8 rounded-xl"
                >
                  <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center mb-6 text-primary">
                    {service.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support Plans */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Support Plans</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Choose the support level that matches your business needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {supportPlans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`glass-effect p-8 rounded-xl relative ${plan.popular ? 'ring-2 ring-primary' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className="font-orbitron font-semibold text-2xl mb-4">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-primary">{plan.price}</span>
                      <span className="text-muted-foreground ml-2">{plan.period}</span>
                    </div>
                    <div className="text-sm text-secondary">Response: {plan.responseTime}</div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-primary-foreground text-xs">✓</span>
                        </div>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={openDrawer} className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}>
                    Choose Plan
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Maintenance Areas */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Maintenance Areas</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Comprehensive maintenance coverage for all your technology needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {maintenanceAreas.map((area, index) => (
                <motion.div
                  key={area.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-effect p-6 rounded-xl text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${area.icon} text-xl text-primary-foreground`}></i>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-3">{area.name}</h3>
                  <p className="text-muted-foreground text-sm">{area.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SLA Benefits */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">SLA Benefits</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Service level agreements that guarantee performance and reliability
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Guaranteed Uptime",
                  description: "99.9% uptime guarantee with compensation for any downtime",
                  icon: "fas fa-clock"
                },
                {
                  title: "Response Time SLA",
                  description: "Guaranteed response times based on your support plan level",
                  icon: "fas fa-stopwatch"
                },
                {
                  title: "Performance Metrics",
                  description: "Detailed reporting on system performance and support metrics",
                  icon: "fas fa-chart-bar"
                }
              ].map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${benefit.icon} text-xl text-primary-foreground`}></i>
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support Process */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Support Process</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Streamlined support process for quick issue resolution
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Issue Report", description: "Report issue through multiple channels" },
                { step: "02", title: "Immediate Response", description: "Acknowledge and assess the issue" },
                { step: "03", title: "Resolution", description: "Implement solution and test thoroughly" },
                { step: "04", title: "Follow-up", description: "Ensure satisfaction and prevent recurrence" }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="font-orbitron font-bold text-primary-foreground">{item.step}</span>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary/10 to-secondary/10">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">
                Need Reliable Support?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Ensure your systems run smoothly with our comprehensive maintenance and support services
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openDrawer} className="btn-primary">
                  Get Support Plan
                </Button>
                <Link href="/contact">
                  <Button className="btn-secondary">
                    Contact Support Team
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
